import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, InfoWindow, OverlayView } from "@react-google-maps/api";

const DEFAULT_CENTER = { lat: 51.5072, lng: -0.1276 }; // Central London
const DEFAULT_ZOOM = 11;
const SINGLE_CLICK_DELAY_MS = 250;
const FIT_PADDING_PX = 48; // px padding for fitBounds

// unified color mapping for all metrics
function valueToColor(value, metric) {
  if (value == null || isNaN(value)) return "#cccccc";

  let min, max, reverse = false;

  if (metric.includes("pct")) {
    min = -0.5; max = 0.5; reverse = true; // ↓ green, ↑ red
  } else if (metric === "median_ppf_6m") {
    min = 0; max = 1400; reverse = true;   // cheaper = green
  } else {
    min = 0; max = 15000; reverse = true;  // cheaper = green
  }

  const clamped = Math.max(min, Math.min(max, value));
  const t = (clamped - min) / (max - min);

  const red = [183, 28, 28], gray = [200, 200, 200], green = [27, 94, 32];
  let c1, c2, localT;

  if (!reverse) {
    if (t < 0.5) { c1 = red;  c2 = gray; localT = t / 0.5; }
    else         { c1 = gray; c2 = green; localT = (t - 0.5) / 0.5; }
  } else {
    if (t < 0.5) { c1 = green; c2 = gray; localT = t / 0.5; }
    else         { c1 = gray;  c2 = red;  localT = (t - 0.5) / 0.5; }
  }

  const r = Math.round(c1[0] + (c2[0] - c1[0]) * localT);
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * localT);
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * localT);
  return `rgb(${r},${g},${b})`;
}

function ChoroplethMap({ geojson, flats, isLoaded, onFeatureClick, level }) {
  const mapRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [shadeMetric, setShadeMetric] = useState("yoy_pct_price");

  const clickTimerRef = useRef(null);

  // Flags to trigger a fit AFTER drilldown loads new GeoJSON
  const shouldFitAfterDrillRef = useRef(false);
  const targetLevelAfterDrillRef = useRef(null);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !geojson) return;
    const map = mapRef.current;

    // Reset data layer and add fresh GeoJSON
    map.data.forEach((f) => map.data.remove(f));
    map.data.addGeoJson(geojson);

    // Style
    map.data.setStyle((feature) => {
      let value = feature.getProperty(shadeMetric);
      if (shadeMetric === "median_ppf_6m" && !value) {
        const ppm2 = feature.getProperty("median_ppm2_6m");
        if (ppm2) value = ppm2 / 10.764;
      }
      const fillColor = valueToColor(value, shadeMetric);
      return {
        fillColor,
        strokeColor: "black",
        strokeWeight: 1,
        fillOpacity: level === "sector" ? 0.25 : 0.6,
        clickable: true,
      };
    });

    const getCode = (feature) => {
      if (level === "districts") {
        return (
          feature.getProperty("district") ||
          feature.getProperty("geography") ||
          feature.getProperty("name")
        );
      }
      return (
        feature.getProperty("sector") ||
        feature.getProperty("geography") ||
        feature.getProperty("name")
      );
    };

    const fitAllCurrentFeatures = () => {
      const b = new window.google.maps.LatLngBounds();
      map.data.forEach((f) => f.getGeometry().forEachLatLng((ll) => b.extend(ll)));
      if (!b.isEmpty()) map.fitBounds(b, FIT_PADDING_PX);
    };

    const openBubble = (evt, feature) => {
      if (level === "sector") return; // no bubble on sector
      const num_price = feature.getProperty("num_price");
      const median_price_6m = feature.getProperty("median_price_6m");
      if (num_price == null && median_price_6m == null) return;

      setSelected({
        position: evt.latLng,
        code: getCode(feature),
        stats: {
          num_price,
          median_price_6m,
          median_ppm2_6m: feature.getProperty("median_ppm2_6m"),
          mom_pct_price: feature.getProperty("mom_pct_price"),
          yoy_pct_price: feature.getProperty("yoy_pct_price"),
          mom_pct_ppm2: feature.getProperty("mom_pct_ppm2"),
          yoy_pct_ppm2: feature.getProperty("yoy_pct_ppm2"),
        },
        type: "polygon",
      });
    };

    // Single click (delayed) → info bubble
    const clickL = map.data.addListener("click", (event) => {
      event.stop();
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        openBubble(event, event.feature);
        clickTimerRef.current = null;
      }, SINGLE_CLICK_DELAY_MS);
    });

    // Double click: ONLY trigger drilldown; fitting happens AFTER new data loads
    const dblDataL = map.data.addListener("dblclick", (event) => {
      event.stop();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      const code = getCode(event.feature);
      if (!code || !onFeatureClick) return;

      if (level === "districts") {
        shouldFitAfterDrillRef.current = true;
        targetLevelAfterDrillRef.current = "district";
        onFeatureClick(code); // parent will fetch district's sectors
      } else if (level === "district") {
        shouldFitAfterDrillRef.current = true;
        targetLevelAfterDrillRef.current = "sector";
        onFeatureClick(code); // parent will fetch that sector
      }
      // At sector level, there is no deeper level — do nothing special.
    });

    // Initial / post-update viewport
    if (level === "districts") {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    } else {
      fitAllCurrentFeatures();
    }

    // If we came here due to drilldown, fit AFTER the new layer is on the map.
    if (shouldFitAfterDrillRef.current && targetLevelAfterDrillRef.current === level) {
      // Two rAFs let Maps fully attach geometry before measuring bounds
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fitAllCurrentFeatures();
          shouldFitAfterDrillRef.current = false;
          targetLevelAfterDrillRef.current = null;
        });
      });
    }

    // Cleanup
    return () => {
      clickL.remove();
      dblDataL.remove();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
    };
  }, [isLoaded, geojson, onFeatureClick, level, shadeMetric]);

  if (!isLoaded) return <div>Loading map…</div>;

  // group flats by lat/lng (to avoid overlap)
  const flatGroups = {};
  flats?.features?.forEach((f) => {
    if (f.geometry.type !== "Point") return;
    const [lng, lat] = f.geometry.coordinates;
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (!flatGroups[key]) flatGroups[key] = [];
    flatGroups[key].push(f);
  });

  return (
    <>
      {/* Toggle control */}
      <div style={{ marginBottom: "8px" }}>
        <label style={{ marginRight: "6px" }}>Color by: </label>
        <select
          value={shadeMetric}
          onChange={(e) => setShadeMetric(e.target.value)}
        >
          <option value="yoy_pct_price">YoY % (Price)</option>
          <option value="mom_pct_price">MoM % (Price)</option>
          <option value="yoy_pct_ppm2">YoY % (£/m²)</option>
          <option value="mom_pct_ppm2">MoM % (£/m²)</option>
          <option value="median_ppm2_6m">Median £/m²</option>
          <option value="median_ppf_6m">Median £/ft²</option>
        </select>
      </div>

      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "400px" }}
        onLoad={(map) => (mapRef.current = map)}
        options={{
          gestureHandling: "greedy",
          disableDoubleClickZoom: true, // prevent native dblclick zoom (we manage it)
          clickableIcons: false,
          minZoom: 9,
        }}
      >
        {/* labels */}
        {geojson?.features?.map((f, idx) => {
          const name =
            f.properties?.geography ||
            f.properties?.sector ||
            f.properties?.district ||
            f.properties?.name;
          if (!name) return null;

          let lat = 0, lng = 0, count = 0;
          const coords = f.geometry.coordinates;
          if (f.geometry.type === "Polygon") {
            (coords[0] || []).forEach(([x, y]) => { lng += x; lat += y; count++; });
          } else if (f.geometry.type === "MultiPolygon") {
            coords.forEach((poly) =>
              (poly[0] || []).forEach(([x, y]) => { lng += x; lat += y; count++; })
            );
          }
          if (count === 0) return null;

          return (
            <OverlayView
              key={idx}
              position={{ lat: lat / count, lng: lng / count }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: "black",
                  pointerEvents: "none",
                  textShadow: "0 0 2px white",
                }}
              >
                {name}
              </div>
            </OverlayView>
          );
        })}

        {/* flats as blue dots (grouped) */}
        {Object.entries(flatGroups).map(([key, group], idx) => {
          const [lat, lng] = key.split(",").map(Number);
          return (
            <OverlayView
              key={`flat-${idx}`}
              position={{ lat, lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected({
                    position: { lat, lng },
                    type: "flatGroup",
                    flats: group.map((f) => f.properties),
                  });
                }}
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "blue",
                  border: "1px solid white",
                  cursor: "pointer",
                }}
              />
            </OverlayView>
          );
        })}

        {/* info window */}
        {selected && selected.position && (
          <InfoWindow
            position={selected.position}
            onCloseClick={() => setSelected(null)}
          >
            <div style={{ fontSize: "12px", maxHeight: "200px", overflowY: "auto" }}>
              {selected.type === "flatGroup" ? (
                <>
                  <strong>{selected.flats.length} sales</strong>
                  {selected.flats.map((flat, i) => (
                    <div key={i} style={{ marginTop: "4px" }}>
                      <div><strong>{flat.address}</strong></div>
                      <div>£{flat.price.toLocaleString()}</div>
                      <div>{flat.date}</div>
                      <div>{flat.property_type}</div>
                      {flat.floor_area && <div>{flat.floor_area} m²</div>}
                      {flat.num_rooms && <div>{flat.num_rooms} rooms</div>}
                      {flat.epc && <div>EPC {flat.epc}</div>}
                    </div>
                  ))}
                </>
              ) : level !== "sector" ? (
                <>
                  <strong>{selected.code}</strong>
                  <div>Transactions of the Month: {selected.stats?.num_price ?? "N/A"}</div>
                  <div>
                    Median Price (6m):{" "}
                    {selected.stats?.median_price_6m
                      ? "£" + selected.stats.median_price_6m.toLocaleString()
                      : "N/A"}
                  </div>
                  <div>
                    Median £/m² (6m):{" "}
                    {selected.stats?.median_ppm2_6m
                      ? "£" + selected.stats.median_ppm2_6m.toLocaleString()
                      : "N/A"}
                  </div>
                  <div>
                    MoM Price:{" "}
                    {selected.stats?.mom_pct_price != null
                      ? selected.stats.mom_pct_price.toFixed(1) + "%"
                      : "N/A"}
                  </div>
                  <div>
                    YoY Price:{" "}
                    {selected.stats?.yoy_pct_price != null
                      ? selected.stats.yoy_pct_price.toFixed(1) + "%"
                      : "N/A"}
                  </div>
                  <div>
                    MoM £/m²:{" "}
                    {selected.stats?.mom_pct_ppm2 != null
                      ? selected.stats.mom_pct_ppm2.toFixed(1) + "%"
                      : "N/A"}
                  </div>
                  <div>
                    YoY £/m²:{" "}
                    {selected.stats?.yoy_pct_ppm2 != null
                      ? selected.stats.yoy_pct_ppm2.toFixed(1) + "%"
                      : "N/A"}
                  </div>
                </>
              ) : null}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </>
  );
}

export default ChoroplethMap;
